import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getRequest } from "../../services/api";
import DashboardLayout from "../DashboardLayout/DashboardLayout";
import "./Dashboard.css";

interface UserTest {
  _id: string;
  test_id: string;
  test_name?: string;
  total_questions?: number;
  time_left?: number;
  test_status?: string;
  blog_title_post_slug?: string;
}

const Dashboard: React.FC = () => {
  const [tests,    setTests]    = useState<UserTest[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [userName, setUserName] = useState("");

  useEffect(() => {
    const raw = localStorage.getItem("user");
    if (raw) {
      const u = JSON.parse(raw);
      setUserName(u.firstname || "");
      const fetchTests = async () => {
        try {
          const res = await getRequest("/auth/get-user-tests", { user_id: u.id }, true);
          if (res?.data?.allTests) setTests(res.data.allTests);
        } catch { /* silent */ }
        finally { setLoading(false); }
      };
      fetchTests();
    } else {
      setLoading(false);
    }
  }, []);

  const submitted = tests.filter(t => t.test_status === "submitted").length;
  const started   = tests.filter(t => t.test_status === "started").length;
  const total     = tests.length;

  const STAT_CARDS = [
    {
      label: "Total Tests",
      value: loading ? "—" : String(total),
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
          <rect x="8" y="2" width="8" height="4" rx="1"/>
        </svg>
      ),
      color: "teal",
      desc: "Tests accessed",
    },
    {
      label: "Submitted",
      value: loading ? "—" : String(submitted),
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      ),
      color: "green",
      desc: "Completed attempts",
    },
    {
      label: "In Progress",
      value: loading ? "—" : String(started),
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      ),
      color: "orange",
      desc: "Tests in progress",
    },
    {
      label: "Score Rate",
      value: submitted > 0 ? `${Math.round((submitted / total) * 100)}%` : "—",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
        </svg>
      ),
      color: "purple",
      desc: "Completion rate",
    },
  ];

  const QUICK_LINKS = [
    { to: "/online-test-series",           label: "Browse Test Series", icon: "📚", desc: "500+ tests available"       },
    { to: "/blogs/all-blogs/mcqs",         label: "Practice MCQs",      icon: "🎯", desc: "10,000+ questions"          },
    { to: "/blogs/all-blogs/posts",        label: "Latest Blogs",       icon: "📰", desc: "Tips, guides & more"        },
    { to: "/blogs/all-blogs/interview_questions", label: "Interview Prep", icon: "🤝", desc: "Top interview Q&As"   },
  ];

  const recentTests = tests.slice(0, 5);

  return (
    <>
      <Helmet><title>Dashboard - Acadebility</title></Helmet>
      <DashboardLayout pageTitle="Dashboard" activePath="/dashboard">
        <div className="db-page">

        {/* ── Welcome banner ── */}
        <div className="db-welcome">
          <div className="db-welcome-text">
            <h1 className="db-welcome-title">
              Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"},{" "}
              <span className="db-welcome-name">{userName || "Student"}</span> 👋
            </h1>
            <p className="db-welcome-sub">
              Ready to learn today? You have {started} test{started !== 1 ? "s" : ""} in progress.
            </p>
          </div>
          <Link to="/online-test-series" className="db-welcome-cta">
            Explore Tests →
          </Link>
        </div>

        {/* ── Stats row ── */}
        <div className="db-stats-row">
          {STAT_CARDS.map(s => (
            <div className={`db-stat-card db-stat-${s.color}`} key={s.label}>
              <div className={`db-stat-icon db-stat-icon-${s.color}`}>{s.icon}</div>
              <div className="db-stat-body">
                <div className="db-stat-value">{s.value}</div>
                <div className="db-stat-label">{s.label}</div>
                <div className="db-stat-desc">{s.desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Two-column: recent tests + quick links ── */}
        <div className="db-two-col">

          {/* Recent Tests */}
          <div className="dsh-card db-section-card">
            <div className="db-section-head">
              <h2 className="db-section-title">Recent Tests</h2>
              <Link to="/tests" className="db-section-link">View all →</Link>
            </div>
            {loading ? (
              <div className="db-loading-rows">
                {[1,2,3].map(i => <div className="db-loading-row" key={i} />)}
              </div>
            ) : recentTests.length === 0 ? (
              <div className="db-empty-state">
                <span className="db-empty-icon">📋</span>
                <p>No tests yet. Start practicing!</p>
                <Link to="/online-test-series" className="db-empty-cta">Browse Tests</Link>
              </div>
            ) : (
              <div className="db-recent-list">
                {recentTests.map(t => (
                  <div className="db-recent-item" key={t._id}>
                    <div className={`db-recent-status db-status-${t.test_status}`}>
                      {t.test_status === "submitted"
                        ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      }
                    </div>
                    <div className="db-recent-info">
                      <div className="db-recent-name">{t.test_name || `Test ${t.test_id}`}</div>
                      <div className="db-recent-meta">
                        {t.total_questions} Qs
                        {t.time_left ? ` · ${Math.ceil(t.time_left / 60)} min left` : ""}
                        <span className={`db-recent-badge db-badge-${t.test_status}`}>
                          {t.test_status === "submitted" ? "Completed" : "In Progress"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick links */}
          <div className="dsh-card db-section-card">
            <div className="db-section-head">
              <h2 className="db-section-title">Quick Access</h2>
            </div>
            <div className="db-quick-grid">
              {QUICK_LINKS.map(q => (
                <Link to={q.to} className="db-quick-item" key={q.label}>
                  <span className="db-quick-icon">{q.icon}</span>
                  <div>
                    <div className="db-quick-label">{q.label}</div>
                    <div className="db-quick-desc">{q.desc}</div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Progress bar visual */}
            <div className="db-progress-block">
              <div className="db-progress-head">
                <span>Overall Progress</span>
                <span>{total > 0 ? `${submitted}/${total}` : "0/0"}</span>
              </div>
              <div className="db-progress-track">
                <div className="db-progress-fill"
                  style={{ width: total > 0 ? `${(submitted / total) * 100}%` : "0%" }} />
              </div>
              <p className="db-progress-note">
                {submitted} test{submitted !== 1 ? "s" : ""} completed out of {total}
              </p>
            </div>
          </div>

        </div>

        </div>
      </DashboardLayout>
    </>
  );
};

export default Dashboard;